<template>
  <div
    class="legend-pane"
    style="font-size: smaller"
  >
    <span class="legend-title">
      <a :href="`${HAZARD_URL}/download?file=HCD_dictionary-2021-09-21.xlsx`">Score Dictionary:</a>
    </span>

    <span class="legend-symbol table-danger">VH</span>
    - Very High
    <span class="legend-symbol table-orange">H</span>
    - High
    <span class="legend-symbol table-warning-light">M</span>
    - Medium
    <span class="legend-symbol table-success">L</span>
    - Low
    <span class="legend-symbol table-secondary">I</span>
    - Inconclusive
    <span class="legend-symbol">N/A</span>
    - Not Applicable

    <span
      class="legend-title"
      style="margin-left: 0.5em"
    >
      Authority:
    </span>

    <span
      :id="`${_uid}-authoritative`"
      class="authoritative"
    >
      Authoritative
      <v-icon
        small
        style="margin-bottom: 10px"
      >
        mdi-information-outline
      </v-icon>
    </span>
    <span
      :id="`${_uid}-screening`"
      class="screening"
    >
      Screening
      <v-icon
        small
        style="margin-bottom: 10px"
      >
        mdi-information-outline
      </v-icon>
    </span>
    <span
      :id="`${_uid}-qsar`"
      class="predicted"
    >
      Predictions
      <v-icon
        small
        style="margin-bottom: 10px"
      >
        mdi-information-outline
      </v-icon>
    </span>

    <b-popover
      custom-class="legend-popover"
      :target="`${_uid}-authoritative`"
      triggers="hover"
    >
      <template #title>Authoritative sources</template>
      <ul class="ml-3">
        <li>European Chemicals Agency (ECHA) Classification Labeling and Packaging (CLP);</li>
        <li>EPA mid-Atlantic Region Human Health Risk-Based Concentrations;</li>
        <li>
          Germany Permanent Senate Commission for the Investigation of Health Hazards of Chemical Compounds in the Work
          Area;
        </li>
        <li>
          World Health Organization International Agency for Research on Cancer (IARC) Monographs on the Evaluation of
          Carcinogenic Risks to Humans;
        </li>
        <li>Integrated Risk Information System (IRIS);</li>
        <li>
          US National Institute for Occupational Safety and Health (NIOSH) list of potential occupational carcinogens;
        </li>
        <li>California Office of Environmental Health Hazard Assessment Proposition 65 List;</li>
        <li>
          EU European Chemicals Agency (ECHA) Registration, Evaluation, Authorization and Restriction of Chemicals
          (REACH) Candidate List of Substances of Very High Concern for Authorization;
        </li>
        <li>US Department of Health and Human Services National Toxicology Program Report on Carcinogens</li>
        <li>Agency for Toxic Substances and Disease Registry (ATSDR)</li>
        <li>Provisional Peer-reviewed Toxicity Values (PPRTV)</li>
      </ul>
      Reference:
      <b-link
        href="https://doi.org/10.1007/s10098-019-01795-w"
        target="paper"
      >
        An automated framework for compiling and integrating chemical hazard data
      </b-link>
    </b-popover>

    <b-popover
      custom-class="legend-popover"
      :target="`${_uid}-screening`"
      triggers="hover"
    >
      <template #title>Screening Sources</template>
      <ul class="ml-3">
        <li>Safe Work Australia Hazardous Chemical Information System (HCIS);</li>
        <li>Canada CNESST Workplace Hazardous Materials Information System (WHMIS);</li>
        <li>Chemidplus;</li>
        <li>Environment and Climate Change Canada Domestic Substance List (DSL);</li>
        <li>Health Canada Priority Substances Lists (2006) (Carcinogenicity);</li>
        <li>Health Canada Priority Substances Lists (2006) (Reproductive Toxicity);</li>
        <li>National Institute of Technology and Evaluation (NITE) of Japan GHS Classification Results;</li>
        <li>
          Department of Occupational Safety and Health Ministry of Human Resources Malaysia, Industry Code of Practice
          on Chemicals Classification and Hazard Communication;
        </li>
        <li>New Zealand Environmental Protection Authority;</li>
        <li>Chemsec Substitute It Now (SIN) List;</li>
        <li>The Endocrine Disruption Exchange (TEDX) List of Potential Endocrine Disruptors;</li>
        <li>US EPA Toxicity Estimation Software Tool (T.E.S.T.); Experimental;</li>
        <li>US EPA Toxic Substances Control Act (TSCA) Work Plan for Chemical Assessments: 2014 Update;</li>
        <li>University of Maryland (UMD) List of Acute Toxins, Teratogens, Carcinogens, or Mutagens</li>
      </ul>
      Reference:
      <b-link
        href="https://doi.org/10.1007/s10098-019-01795-w"
        target="paper"
      >
        An automated framework for compiling and integrating chemical hazard data
      </b-link>
    </b-popover>

    <b-popover
      custom-class="legend-popover"
      :target="`${_uid}-qsar`"
      triggers="hover"
    >
      <template #title>Predictions</template>
      <ul class="ml-3">
        <li>US EPA Toxicity Estimation Software Tool (T.E.S.T.) Predicted</li>
        <li>SEEM: Systematic Empirical Evaluation of Models (SEEM)</li>
        <li>Open (Quantitative) Structure-activity/property Relationship App (OPERA) Predicted</li>
        <li>Estimation Programs Interface Suite (EPI Suite) Predicted</li>
        <li>Ministry of Environment and Food of Denmark Predicted</li>
      </ul>
      Reference:
      <br />
      <b-link
        href="https://doi.org/10.1007/s10098-019-01795-w"
        target="paper"
      >
        An automated framework for compiling and integrating chemical hazard data
      </b-link>
      <br />
      <b-link
        href="https://pubs.acs.org/doi/full/10.1021/es400482g"
        target="models"
      >
        High-Throughput Models for Exposure-Based Chemical Prioritization in the ExpoCast Project
      </b-link>
    </b-popover>
  </div>
</template>

<script>
import { HAZARD_URL } from '@/main'

export default {
  computed: {
    HAZARD_URL() {
      return HAZARD_URL
    },
  },
}
</script>

<style lang="scss" scoped>
.legend-pane {
  display: inline-block;
  float: right;
  text-align: right;
  margin: 0.5em 0.2em 0.2em 0;
}

.legend-title {
  font-style: italic;
}

.legend-symbol {
  padding: 0.3em;
}

.legend-popover {
  max-width: 800px !important;
  min-width: 600px;
  width: max-content !important;
}
</style>
